import { baseApiUrl } from 'src/config';
import { getAccessToken, getRealmName } from 'src/routes/AuthContext';

/**
 * BRK-1852: MDS worksheet endpoints (contract v1.1 §5.2-§5.4, backend merged
 * in BRK-1848). Types mirror document-service DTOs field-for-field.
 */

export type MdsWorksheetAnchor = {
  assessmentUuid: string;
  assessmentType: string | null;
  assessmentTypeRaw: string | null;
  ard: string | null;
  latestVersion: number | null;
  lastSubmittedByRole: string | null;
  // BRK-1898: resolved display name; null when the iamId is unresolvable
  lastSubmittedByName: string | null;
  lastSubmittedAt: string | null;
};

export type MdsScoreOption = { value: string; description: string };

export type MdsColumnSource = {
  // null for an MDS_COLLECTED source (BRK-1866); always a number for GG_COLLECTED
  ggCollectedId: number | null;
  documentType: string | null;
  dateOfService: string | null;
  levelType: string | null;
  inObservationWindow: boolean | null;
};

export type MdsWorksheetColumn = {
  mdsColumn: string;
  currentValue: string | null;
  sourceType: string | null;
  source: MdsColumnSource | null;
  enteredByRole: string | null;
  // BRK-1898: resolved display name; null when the iamId is unresolvable
  enteredByName: string | null;
  enteredAt: string | null;
  fromVersion: number | null;
};

export type MdsGgCandidate = {
  ggCollectedId: number;
  formSubmissionUuid: string | null;
  documentType: string | null;
  discipline: string | null;
  dateOfService: string | null;
  priorLevel: string | null;
  currentLevel: string | null;
  dischargeGoal: string | null;
  inObservationWindow: boolean | null;
};

/**
 * BRK-1866: an mds_item_collected value this Section K item can be pulled from.
 * Unlike a GG candidate (up to three levels), an MDS candidate carries a single
 * value. The backend always sends `mdsCandidates` (empty for GG items and K
 * items with no collected data).
 */
export type MdsItemCandidate = {
  mdsItemCollectedId: number;
  formSubmissionUuid: string | null;
  documentType: string | null;
  discipline: string | null;
  dateOfService: string | null;
  value: string;
  inObservationWindow: boolean | null;
};

export type MdsWorksheetItem = {
  itemCode: string;
  label: string;
  discipline: string | null;
  scoreType: string;
  usedForFunctionScore: boolean | null;
  sortOrder: number | null;
  columns: MdsWorksheetColumn[];
  ggCandidates: MdsGgCandidate[];
  mdsCandidates: MdsItemCandidate[];
};

export type MdsWorksheetSection = {
  sectionCode: string;
  sectionName: string;
  canEdit: boolean;
  items: MdsWorksheetItem[];
};

export type MdsWorksheetAggregate = {
  assessment: {
    assessmentUuid: string;
    assessmentType: string | null;
    assessmentTypeRaw: string | null;
    ard: string | null;
    completeByDate: string | null;
  };
  patient: {
    patientUuid: string;
    patientName: string | null;
    mrn: string | null;
    facilityName: string | null;
    facilityUuid: string | null;
  };
  canPrint: boolean;
  versions: MdsWorksheetVersion[];
  scoreOptions: Record<string, MdsScoreOption[]>;
  sections: MdsWorksheetSection[];
};

export type MdsWorksheetVersion = {
  uuid: string;
  versionNumber: number;
  submittedByRole: string | null;
  // BRK-1898: resolved display name; null when the iamId is unresolvable
  submittedByName: string | null;
  submittedAt: string | null;
  note: string | null;
};

/** BRK-1856: one field the caller's submission overwrote (accept-and-warn LWW) */
export type MdsConflictItem = {
  itemCode: string;
  mdsColumn: string;
  yourValue: string | null;
  previousValue: string | null;
  previousByRole: string | null;
  // BRK-1898: resolved display name; null when the iamId is unresolvable
  previousByName: string | null;
  previousVersion: number | null;
};

export type MdsSubmitWorksheetRequest = {
  baseVersionNumber: number | null;
  note: string | null;
  items: {
    sectionCode: string;
    itemCode: string;
    mdsColumn: string;
    itemValue: string | null;
    // 'MANUAL' | 'GG_COLLECTED' | 'MDS_COLLECTED' (BRK-1866)
    sourceType: string;
    sourceGgCollectedId?: number | null;
    sourceLevelType?: string | null;
    // BRK-1866: for an applied K candidate — the source document (server
    // re-verifies the value + snapshots documentType/dateOfService itself)
    sourceFormSubmissionUuid?: string | null;
  }[];
};

export type MdsSubmitWorksheetResponse = {
  submissionUuid: string;
  versionNumber: number;
  conflictItems: MdsConflictItem[];
};

export type MdsSubmissionItem = {
  sectionCode: string;
  itemCode: string;
  mdsColumn: string;
  itemValue: string | null;
  sourceType: string;
  sourceGgCollectedId: number | null;
  sourceLevelType: string | null;
  sourceDocumentType: string | null;
  sourceDateOfService: string | null;
};

export type MdsSubmissionItems = {
  submissionUuid: string;
  versionNumber: number;
  items: MdsSubmissionItem[];
};

/** BRK-1859: print result — a presigned URL to the generated worksheet PDF */
export type MdsPrintResult = {
  pdfUrl: string;
  versionNumber: number | null;
  printedByRole: string | null;
  printedAt: string | null;
};

const headers = async () => ({
  Authorization: `Bearer ${await getAccessToken()}`,
  'x-tenant-id': getRealmName() || '',
});

const parseEnvelope = async (response: Response, fallback: string) => {
  let body: any = null;
  try {
    body = await response.json();
  } catch (e) {
    throw new Error(`${fallback} (HTTP ${response.status})`);
  }
  if (body?.code !== 'OK') {
    throw new Error(body?.message || fallback);
  }
  return body.data;
};

export const getMdsWorksheetAnchors = async (
  patientUuid: string
): Promise<MdsWorksheetAnchor[]> => {
  const response = await fetch(
    `${baseApiUrl}/document/mds-dashboard/patients/${patientUuid}/worksheets`,
    { method: 'GET', headers: await headers() }
  );
  return parseEnvelope(response, 'Failed to load MDS worksheets');
};

export const createMdsWorksheet = async (body: {
  patientUuid: string;
  assessmentType: string;
  ard: string;
}): Promise<{ assessmentUuid: string }> => {
  const response = await fetch(`${baseApiUrl}/document/mds-dashboard/worksheets`, {
    method: 'POST',
    headers: { ...(await headers()), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseEnvelope(response, 'Failed to create MDS worksheet');
};

export const getMdsWorksheetAggregate = async (
  assessmentUuid: string
): Promise<MdsWorksheetAggregate> => {
  const response = await fetch(
    `${baseApiUrl}/document/mds-dashboard/worksheets/${assessmentUuid}`,
    { method: 'GET', headers: await headers() }
  );
  return parseEnvelope(response, 'Failed to load MDS worksheet');
};

export const submitMdsWorksheet = async (
  assessmentUuid: string,
  body: MdsSubmitWorksheetRequest
): Promise<MdsSubmitWorksheetResponse> => {
  const response = await fetch(
    `${baseApiUrl}/document/mds-dashboard/worksheets/${assessmentUuid}/submissions`,
    {
      method: 'POST',
      headers: { ...(await headers()), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  return parseEnvelope(response, 'Failed to submit MDS worksheet');
};

export const getMdsSubmissionItems = async (
  submissionUuid: string
): Promise<MdsSubmissionItems> => {
  const response = await fetch(
    `${baseApiUrl}/document/mds-dashboard/submissions/${submissionUuid}/items`,
    { method: 'GET', headers: await headers() }
  );
  return parseEnvelope(response, 'Failed to load submission');
};

export const printMdsWorksheet = async (assessmentUuid: string): Promise<MdsPrintResult> => {
  const response = await fetch(
    `${baseApiUrl}/document/mds-dashboard/worksheets/${assessmentUuid}/print`,
    { method: 'POST', headers: await headers() }
  );
  return parseEnvelope(response, 'Failed to generate the MDS worksheet PDF');
};
